import { lazy, Suspense, useEffect, useState, useMemo, useCallback } from 'react'
import { useLocation, useTiles } from '../hooks'
import { LocationInput, StationDetailSheet, SortDropdown } from '../components'
import { DraggableSheet, type SheetPosition } from '../components/DraggableSheet'
import { CompactStationCard } from '../components/CompactStationCard'
import { Loader2 } from 'lucide-react'
import type { StationWithDistance, SortOption, TypicalSpend } from '@fuelfinder/shared'

// Lazy load the MapView component (includes Leaflet ~40KB gzipped)
const MapView = lazy(() =>
  import('../components/MapView').then((m) => ({ default: m.MapView }))
)

interface HybridPageProps {
  fuelType: 'E10' | 'B7'
  sortBy: SortOption
  onSortChange: (sort: SortOption) => void
  typicalSpendPounds?: TypicalSpend
  refreshTrigger?: number
  searchPostcode?: string | null
  searchCoords?: { lat: number; lng: number } | null
}

/** Height of fixed header elements (header + location search + fuel toggle) */
const FIXED_HEADER_HEIGHT = 160

/** Sheet snap point percentages (must match DraggableSheet) */
const SHEET_SNAP_POINTS: Record<SheetPosition, number> = {
  minimized: 0.15,
  half: 0.55,
  maximized: 0.90,
}

/**
 * Loading spinner for map lazy load
 */
function MapLoadingSpinner() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={32} className="text-green-600 animate-spin" />
        <p className="text-slate-600 dark:text-slate-300 text-sm">Loading map...</p>
      </div>
    </div>
  )
}

/**
 * Loading overlay for when stations are still being fetched
 */
function StationsLoadingOverlay() {
  return (
    <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center z-[500]">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
        <Loader2 size={20} className="text-green-600 animate-spin" />
        <span className="text-slate-700 dark:text-slate-200 text-sm font-medium">
          Loading stations...
        </span>
      </div>
    </div>
  )
}

/**
 * Get price percentile for a station compared to others
 */
function getPricePercentile(
  station: StationWithDistance,
  allStations: StationWithDistance[],
  fuelType: 'E10' | 'B7'
): number {
  const prices = allStations
    .map((s) => s.prices[fuelType])
    .filter((p): p is number => p !== null)
    .sort((a, b) => a - b)

  const stationPrice = station.prices[fuelType]
  if (stationPrice === null || prices.length === 0) return 50

  const index = prices.findIndex((p) => p >= stationPrice)
  if (index === -1) return 100
  return (index / prices.length) * 100
}

/**
 * Sort stations based on sort option
 */
function sortStations(
  stations: StationWithDistance[],
  sortBy: SortOption,
  fuelType: 'E10' | 'B7',
  typicalSpendPounds?: TypicalSpend
): StationWithDistance[] {
  const sorted = [...stations]

  switch (sortBy) {
    case 'nearest':
      sorted.sort((a, b) => a.distanceMiles - b.distanceMiles)
      break
    case 'cheapest':
      sorted.sort((a, b) => {
        const priceA = a.prices[fuelType]
        const priceB = b.prices[fuelType]
        if (priceA === null && priceB === null) return 0
        if (priceA === null) return 1
        if (priceB === null) return -1
        return priceA - priceB
      })
      break
    case 'best-value':
      // Best value considers both price and distance
      // Formula: savings vs cheapest - (distance * cost_per_mile)
      const cheapestPrice = Math.min(
        ...stations.map((s) => s.prices[fuelType]).filter((p): p is number => p !== null)
      )
      const litresPerFill = typicalSpendPounds ? (typicalSpendPounds / (cheapestPrice / 100)) : 40
      const costPerMile = 0.15 // Rough estimate in pounds

      sorted.sort((a, b) => {
        const priceA = a.prices[fuelType]
        const priceB = b.prices[fuelType]
        if (priceA === null && priceB === null) return 0
        if (priceA === null) return 1
        if (priceB === null) return -1

        // Calculate value score (higher is better)
        const savingsA = ((cheapestPrice - priceA) / 100) * litresPerFill
        const travelCostA = a.distanceMiles * 2 * costPerMile // Round trip
        const valueA = savingsA - travelCostA

        const savingsB = ((cheapestPrice - priceB) / 100) * litresPerFill
        const travelCostB = b.distanceMiles * 2 * costPerMile
        const valueB = savingsB - travelCostB

        return valueB - valueA // Higher value first
      })
      break
  }

  return sorted
}

/**
 * Hybrid page combining map view with draggable station list.
 * Single source of truth for location and station data.
 */
export function HybridPage({
  fuelType,
  sortBy,
  onSortChange,
  typicalSpendPounds,
  refreshTrigger,
  searchPostcode,
  searchCoords,
}: HybridPageProps) {
  const {
    location,
    gpsLocation,
    isLoading: locationLoading,
    error: locationError,
    setPostcode,
    setCoordinates,
    resetToGps,
  } = useLocation()

  // Separate view center for map panning (doesn't affect searched location marker)
  const [viewCenter, setViewCenter] = useState<{ lat: number; lng: number } | null>(null)

  // Handle search postcode from header
  useEffect(() => {
    if (searchPostcode) {
      setPostcode(searchPostcode)
      setViewCenter(null) // Reset view center when user searches
    }
  }, [searchPostcode, setPostcode])

  // Handle search coordinates from header
  useEffect(() => {
    if (searchCoords) {
      setCoordinates(searchCoords.lat, searchCoords.lng)
      setViewCenter(null) // Reset view center when user searches
    }
  }, [searchCoords, setCoordinates])

  // Use view center for tile fetching if user has panned, otherwise use searched location
  const tileLocation = useMemo(() => {
    if (viewCenter) {
      return { lat: viewCenter.lat, lng: viewCenter.lng, source: 'geolocation' as const, timestamp: Date.now() }
    }
    return location
  }, [viewCenter?.lat, viewCenter?.lng, location])
  const { stations, isLoading: tilesLoading, error: tilesError, refetch } = useTiles(tileLocation)
  const [selectedStation, setSelectedStation] = useState<StationWithDistance | null>(null)
  const [sheetPosition, setSheetPosition] = useState<SheetPosition>('half')
  const [availableHeight, setAvailableHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight - FIXED_HEADER_HEIGHT : 600
  )

  // Sorted stations
  const sortedStations = useMemo(
    () => sortStations(stations, sortBy, fuelType, typicalSpendPounds),
    [stations, sortBy, fuelType, typicalSpendPounds]
  )

  // Update available height on resize
  useEffect(() => {
    const updateHeight = () => {
      setAvailableHeight(window.innerHeight - FIXED_HEADER_HEIGHT)
    }
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  // Trigger refetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetch()
    }
  }, [refreshTrigger, refetch])

  // Handle station marker click on map
  const handleStationClick = (stationId: string) => {
    const station = stations.find((s) => s.id === stationId)
    if (station) {
      setSelectedStation(station)
    }
  }

  // Handle station tap in list
  const handleListStationClick = (station: StationWithDistance) => {
    setSelectedStation(station)
  }

  // Handle map panning - update view center for tile fetching
  const handleMapMove = useCallback((lat: number, lng: number) => {
    setViewCenter({ lat, lng })
  }, [])

  // Handle recenter - reset view center and go back to GPS
  const handleRecenter = useCallback(() => {
    setViewCenter(null)
    resetToGps()
  }, [resetToGps])

  // Show postcode input if no location
  if (!location && !locationLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <LocationInput onSubmit={setPostcode} isLoading={locationLoading} />
        {locationError && (
          <p className="text-center text-red-600 dark:text-red-400 text-sm px-4 -mt-4">
            {locationError}
          </p>
        )}
      </div>
    )
  }

  // Show loading state while getting location
  if (locationLoading && !location) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="animate-pulse w-16 h-16 bg-slate-200 dark:bg-slate-800 rounded-full mb-4" />
          <p className="text-slate-600 dark:text-slate-300">Getting your location...</p>
        </div>
      </div>
    )
  }

  // Calculate sheet height for map centering offset
  const sheetHeight = availableHeight * SHEET_SNAP_POINTS[sheetPosition]

  // Sheet header content with station count and sort dropdown
  const sheetHeaderContent = (
    <div className="flex items-center justify-between w-full">
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
        {stations.length === 1 ? '1 station nearby' : `${stations.length} stations nearby`}
      </span>
      <SortDropdown value={sortBy} onChange={onSortChange} />
    </div>
  )

  return (
    <>
      {/* Fixed container for map and sheet */}
      <div
        className="fixed left-0 right-0 bottom-0 max-w-2xl mx-auto"
        style={{ top: FIXED_HEADER_HEIGHT }}
      >
        {/* Map fills the entire container */}
        <div className="hybrid-map-container">
          {/* Error banner */}
          {tilesError && (
            <div className="absolute top-4 left-4 right-4 z-[500]">
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-2 shadow-lg">
                <p className="text-red-800 dark:text-red-200 text-sm">{tilesError}</p>
              </div>
            </div>
          )}

          {/* Loading overlay while fetching stations */}
          {tilesLoading && stations.length === 0 && <StationsLoadingOverlay />}

          {/* Lazy loaded map */}
          <Suspense fallback={<MapLoadingSpinner />}>
            <MapView
              stations={stations}
              userLocation={location ? { lat: location.lat, lng: location.lng } : null}
              userLocationIsGps={location?.source === 'geolocation'}
              gpsLocation={gpsLocation ? { lat: gpsLocation.lat, lng: gpsLocation.lng } : null}
              fuelType={fuelType}
              onStationClick={handleStationClick}
              onRecenter={handleRecenter}
              onMapMove={handleMapMove}
              bottomSheetHeight={sheetHeight}
            />
          </Suspense>
        </div>

        {/* Draggable sheet with station list */}
        <DraggableSheet
          position={sheetPosition}
          onPositionChange={setSheetPosition}
          availableHeight={availableHeight}
          headerContent={sheetHeaderContent}
        >
          {sortedStations.length === 0 && !tilesLoading ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <p className="text-slate-500 dark:text-slate-400 text-center">
                No stations found in your area
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {sortedStations.map((station) => (
                <CompactStationCard
                  key={station.id}
                  station={station}
                  fuelType={fuelType}
                  pricePercentile={getPricePercentile(station, stations, fuelType)}
                  onClick={() => handleListStationClick(station)}
                />
              ))}
            </div>
          )}
        </DraggableSheet>
      </div>

      {/* Station detail sheet */}
      {selectedStation && (
        <StationDetailSheet
          station={selectedStation}
          fuelType={fuelType}
          pricePercentile={getPricePercentile(selectedStation, stations, fuelType)}
          onClose={() => setSelectedStation(null)}
        />
      )}
    </>
  )
}
